import { Trash2Icon } from 'lucide-react';

import type { DeskPackageConfiguration, RateCardUnit } from '@mds/shared/types/rate-card';

import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import { controlCls, InlineError } from '@/components/ui/field';
import Chip from '@/components/ui/chip';
import IconButton from '@mds/shared/ui/icon-button';
import Select from '@mds/shared/ui/select';
import { Table, Row, Cell } from '@/components/ui/table';
import { isPackageUnit } from '@/domain/packages';
import { type FieldBinder, useInlineField } from '@/lib/validation';

/** Which of the card's three lists a row belongs to. */
export type Group = 'spaceRates' | 'deskPackages' | 'addOns';

export interface DraftItem {
  /**
   * The stable business identifier, not a database row id. Resources and
   * bookings reference it, so it is never shown or edited: an existing row
   * keeps its own through a rename, and a new one is named after itself when
   * the card is published.
   */
  id: string;
  name: string;
  /** Dollars, as typed. Converted to integer cents when the card is published. */
  amount: string;
  unit: RateCardUnit;
  note: string;
  /** Added in this editing session, so it has no id of its own yet. */
  isNew: boolean;
  /** Desk packages only: the rules sold with it. Always sent in full once shown. */
  package?: DeskPackageConfiguration;
}

/** The most rows one list takes; the API refuses a longer one. */
const MAX_ROWS = 100;

/** One of the card's three lists, edited in place. */
export default function RateTable({
  title,
  hint,
  group,
  rows,
  units,
  editable,
  onChange,
  onEditPackage,
  field,
}: {
  title: string;
  hint: string;
  /** Where these rows sit in the draft, which is where their errors are. */
  group: Group;
  rows: DraftItem[];
  units: readonly RateCardUnit[];
  editable: boolean;
  onChange: (rows: DraftItem[]) => void;
  /** Desk packages only: opens a row's rules. Adds the Rules column. */
  onEditPackage?: (index: number) => void;
  /** The page's validation, checked against `rateCardSchema`. */
  field: FieldBinder;
}) {
  const inline = useInlineField(field);

  // Disabled inputs otherwise look exactly like editable ones, which reads as a
  // broken field rather than a permission boundary.
  const cell = editable ? controlCls : `${controlCls} border-hairline bg-raised text-ink-secondary`;

  const set = (index: number, patch: Partial<DraftItem>) =>
    onChange(
      rows.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        // A package's term is counted in the unit it is priced by, so the two
        // move together.
        if (patch.unit && next.package && isPackageUnit(patch.unit))
          next.package = {
            ...next.package,
            duration: { ...next.package.duration, unit: patch.unit },
          };
        return next;
      }),
    );

  const add = () =>
    onChange([
      ...rows,
      { id: '', name: '', amount: '0.00', unit: units[0], note: '', isNew: true },
    ]);

  return (
    <Card
      title={title}
      action={
        editable ? (
          <Button variant="quiet" disabled={rows.length >= MAX_ROWS} onClick={add}>
            Add row
          </Button>
        ) : null
      }
    >
      <p className="m-0 mb-4 text-base text-ink-muted">{hint}</p>

      {rows.length === 0 ? (
        <p className="m-0 text-base text-ink-muted">
          Nothing here. {editable ? 'Add a row to price something.' : ''}
        </p>
      ) : (
        <Table
          head={
            onEditPackage
              ? ['Name', 'Amount (USD)', 'Unit', 'Rules', 'Note', '']
              : ['Name', 'Amount (USD)', 'Unit', 'Note', '']
          }
        >
          {rows.map((row, index) => {
            const at = `${group}.${index}`;
            const name = inline(`${at}.name`);
            const amount = inline(`${at}.amount`);
            const unit = inline(`${at}.unit`);
            const note = inline(`${at}.note`);
            const rules = inline(`${at}.package`, { within: true });
            return (
              <Row key={index}>
                <Cell>
                  <div className="flex flex-col gap-1.5">
                    <input
                      aria-label="Rate name"
                      className={cell + ' w-full sm:min-w-[160px]'}
                      value={row.name}
                      disabled={!editable}
                      onChange={(e) => set(index, { name: e.target.value })}
                      {...name.props}
                    />
                    <InlineError {...name.message} />
                  </div>
                </Cell>
                <Cell>
                  <div className="flex flex-col gap-1.5">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      inputMode="decimal"
                      aria-label="Rate amount in dollars"
                      className={cell + ' w-full tabular-nums sm:w-28'}
                      value={row.amount}
                      disabled={!editable}
                      onChange={(e) => set(index, { amount: e.target.value })}
                      {...amount.props}
                    />
                    <InlineError {...amount.message} />
                  </div>
                </Cell>
                <Cell>
                  <div className="flex flex-col gap-1.5">
                    <Select
                      aria-label="Rate unit"
                      value={row.unit}
                      disabled={!editable}
                      onChange={(unit) => set(index, { unit: unit as RateCardUnit })}
                      options={units.map((u) => ({ value: u, label: u }))}
                      {...unit.props}
                    />
                    <InlineError {...unit.message} />
                  </div>
                </Cell>
                {onEditPackage && (
                  <Cell>
                    {/* The rules are edited in a dialog, so a problem in them is
                      reported here, on the row, and the group takes focus. */}
                    <div
                      role="group"
                      aria-label="Rules"
                      tabIndex={-1}
                      className="flex flex-col items-start gap-1.5 sm:min-w-[200px]"
                      {...rules.props}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        {row.package && !row.package.isAvailable && (
                          <Chip className="border border-hairline bg-raised text-ink-muted">
                            Off sale
                          </Chip>
                        )}
                        <Button variant="quiet" onClick={() => onEditPackage(index)}>
                          {editable ? 'Edit rules' : 'Rules'}
                        </Button>
                      </div>
                      <InlineError
                        {...rules.message}
                        error={
                          rules.message.error && 'These rules need fixing. Open them to see what.'
                        }
                      />
                    </div>
                  </Cell>
                )}
                <Cell>
                  <div className="flex flex-col gap-1.5">
                    <input
                      aria-label="Rate note"
                      className={cell + ' w-full sm:min-w-[180px]'}
                      value={row.note}
                      disabled={!editable}
                      onChange={(e) => set(index, { note: e.target.value })}
                      {...note.props}
                    />
                    <InlineError {...note.message} />
                  </div>
                </Cell>
                <Cell>
                  {editable && (
                    <IconButton
                      tone="danger"
                      label={`Remove ${row.name || 'this row'}`}
                      onClick={() => onChange(rows.filter((_, i) => i !== index))}
                    >
                      <Trash2Icon className="size-6" strokeWidth={2.2} aria-hidden="true" />
                    </IconButton>
                  )}
                </Cell>
              </Row>
            );
          })}
        </Table>
      )}
    </Card>
  );
}
